import CloseButton from '@components/CloseButton'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  LoadingDialog,
} from '@components/DewiDialog'
import { Text } from '@components/Helpers'
import { useGovernance } from '@hooks/useGovernance'
import { Box, Button, styled } from '@mui/material'
import React, { useCallback, useState } from 'react'
import CastVoteIcon from '@assets/castVoteIcon.svg'
import { useTranslation } from 'react-i18next'
import { useProposal } from '@helium/modular-governance-hooks'
import { PublicKey } from '@solana/web3.js'
import DewiButton from '@components/DewiButton'
import { useSolana } from '@dewicats/connect-button'
import { useRouter } from 'next/navigation'

const ForButton = styled(Button)({
  border: '1px solid var(--dewi_colors_green_7)',
  backgroundColor: 'var(--dewi_colors_green_8) !important',
  textTransform: 'capitalize',
  height: '2.625rem',
  borderRadius: '8rem',
})

const AgainstButton = styled(Button)({
  border: '1px solid var(--dewi_colors_red_7)',
  backgroundColor: 'var(--dewi_colors_red_8) !important',
  textTransform: 'capitalize',
  height: '2.625rem',
  borderRadius: '8rem',
})

const AbstainButton = styled(Button)({
  border: '1px solid var(--dewi_colors_gray_7)',
  backgroundColor: 'var(--dewi_colors_gray_8) !important',
  textTransform: 'capitalize',
  height: '2.625rem',
  borderRadius: '8rem',
})

const OrangeButton = styled(Button)({
  border: '1px solid var(--dewi_colors_orange_4)',
  backgroundColor: 'var(--dewi_colors_orange_5) !important',
  textTransform: 'capitalize',
  height: '2.625rem',
  borderRadius: '8rem',
})

const VioletButton = styled(Button)({
  border: '1px solid var(--dewi_colors_violet_4)',
  backgroundColor: 'var(--dewi_colors_violet_5) !important',
  textTransform: 'capitalize',
  height: '2.625rem',
  borderRadius: '8rem',
})

const PinkButton = styled(Button)({
  border: '1px solid var(--dewi_colors_pink_4)',
  backgroundColor: 'var(--dewi_colors_pink_5) !important',
  textTransform: 'capitalize',
  height: '2.625rem',
  borderRadius: '8rem',
})

const CyanButton = styled(Button)({
  border: '1px solid var(--dewi_colors_cyan_4)',
  backgroundColor: 'var(--dewi_colors_cyan_5) !important',
  textTransform: 'capitalize',
  height: '2.625rem',
  borderRadius: '8rem',
})

const BoldText = {
  fontWeight: 'bold',
}

type ProposalDialogProps = {
  proposalKey: string
}

const ProposalDialog = ({ proposalKey }: ProposalDialogProps) => {
  const { t } = useTranslation()
  const { publicKey } = useSolana()
  const proposal = useProposal(new PublicKey(proposalKey))
  const {
    setVoteDialog,
    voteDialog,
    vote,
    transactionState,
    clearTransaction,
  } = useGovernance()
  const [voting, setVoting] = useState(false)
  const router = useRouter()

  const viewOnExplorer = useCallback(() => {
    window.open(
      `https://explorer.solana.com/address/${publicKey.toBase58()}`,
      '_blank',
    )
  }, [publicKey])

  const handleClose = useCallback(() => {
    setVoteDialog(false)
  }, [setVoteDialog])

  const handleDialogClosed = useCallback(() => {}, [])

  const onCloseHeader = useCallback(() => {
    setVoteDialog(false)
    clearTransaction()
  }, [setVoteDialog])

  const handleVote = useCallback(
    (choice: number) => async () => {
      if (!(proposal as any)?.info?.pubkey) return
      setVoting(true)
      try {
        await vote((proposal as any)?.info?.pubkey, choice)
        router.refresh()
        setVoteDialog(false)
      } catch (error) {
        console.error(error)
      }
      setVoting(false)
    },
    [proposal, vote],
  )

  const VoteButton = useCallback(
    ({ index }: { index: number }) => {
      if (proposal?.info?.choices.length === 3) {
        switch (index) {
          case 0:
            return (
              <ForButton onClick={handleVote(index)} variant="contained">
                <Text sx={BoldText}>{proposal?.info?.choices[index].name}</Text>
              </ForButton>
            )
          case 1:
            return (
              <AgainstButton onClick={handleVote(index)} variant="contained">
                <Text sx={BoldText}>
                  <Text sx={BoldText}>
                    {proposal?.info?.choices[index].name}
                  </Text>
                </Text>
              </AgainstButton>
            )
          case 2:
            return (
              <AbstainButton onClick={handleVote(index)} variant="contained">
                <Text sx={BoldText}>
                  <Text sx={BoldText}>
                    {proposal?.info?.choices[index].name}
                  </Text>
                </Text>
              </AbstainButton>
            )
        }
      }

      switch (index) {
        case 0:
          return (
            <CyanButton onClick={handleVote(index)} variant="contained">
              <Text sx={BoldText}>{proposal?.info?.choices[index].name}</Text>
            </CyanButton>
          )
        case 1:
          return (
            <OrangeButton onClick={handleVote(index)} variant="contained">
              <Text sx={BoldText}>
                <Text sx={BoldText}>{proposal?.info?.choices[index].name}</Text>
              </Text>
            </OrangeButton>
          )
        case 2:
          return (
            <VioletButton onClick={handleVote(index)} variant="contained">
              <Text sx={BoldText}>
                <Text sx={BoldText}>{proposal?.info?.choices[index].name}</Text>
              </Text>
            </VioletButton>
          )
        case 3:
          return (
            <PinkButton onClick={handleVote(index)} variant="contained">
              <Text sx={BoldText}>
                <Text sx={BoldText}>{proposal?.info?.choices[index].name}</Text>
              </Text>
            </PinkButton>
          )
        default:
          return (
            <DewiButton onClick={handleVote(index)} variant="contained">
              <Text sx={BoldText}>
                <Text sx={BoldText}>{proposal?.info?.choices[index].name}</Text>
              </Text>
            </DewiButton>
          )
      }
    },
    [proposal],
  )

  return (
    <Dialog
      open={voteDialog}
      onClose={handleClose}
      onExited={handleDialogClosed}
    >
      {voting && <LoadingDialog />}
      {!voting && (
        <>
          <DialogHeader>
            <Box alignSelf="flex-end">
              <CloseButton onClick={onCloseHeader} />
            </Box>
          </DialogHeader>
          {!transactionState.error && (
            <>
              <DialogContent>
                <CastVoteIcon />
                <Box>
                  <Text
                    sx={{
                      fontSize: '18px',
                    }}
                  >
                    {t('dao.proposalDetails.castVote')}
                  </Text>
                </Box>
              </DialogContent>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--dewi_spacing_3)',
                  marginBottom: { xs: 'var(--dewi_spacing_12)', md: 0 },
                }}
              >
                {proposal?.info?.choices.map((choice, index) => (
                  <VoteButton key={choice.name} index={index} />
                ))}
              </Box>
            </>
          )}
          {transactionState.error && (
            <Box
              sx={{
                marginBottom: { xs: 'var(--dewi_spacing_12)', md: 0 },
              }}
            >
              <Text
                sx={{
                  fontSize: '18px',
                  textAlign: 'center',
                }}
              >
                {t('dao.proposalDetails.errorCastVote')}
              </Text>
              <Text
                sx={{
                  fontSize: '12px',
                  textAlign: 'center',
                }}
              >
                {t('dao.proposalDetails.clickOnErrorLink')}
              </Text>

              <DewiButton onClick={viewOnExplorer} variant="outlined">
                <Text sx={BoldText}>
                  {t('dao.proposalDetails.viewOnExplorer')}
                </Text>
              </DewiButton>
            </Box>
          )}
        </>
      )}
    </Dialog>
  )
}

export default ProposalDialog
